import React from 'react';
import { Award, BookOpen, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import profileData from '../data/profileData.json';

const About = () => {
  const { personal, education, certifications, awards } = profileData;

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-[#1e3a5f] mb-4">About Me</h1>
          <div className="h-1 w-20 bg-[#2563eb]"></div>
        </div>

        {/* About Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl text-[#1e3a5f]">Professional Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {personal.summary}
                </p>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-blue-100 p-2 rounded-md mt-1">
                      <MapPin size={20} className="text-[#2563eb]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1e3a5f] mb-1">Location</h4>
                      <p className="text-gray-600">{personal.location}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-blue-100 p-2 rounded-md mt-1">
                      <BookOpen size={20} className="text-[#2563eb]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1e3a5f] mb-1">Current Role</h4>
                      <p className="text-gray-600">{personal.currentRole}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Education Card */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="text-xl text-[#1e3a5f]">Education</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <h4 className="font-semibold text-[#1e3a5f]">{education.degree}</h4>
                  <p className="text-gray-600">{education.specialization}</p>
                  <p className="text-gray-500 text-sm">{education.university}</p>
                  <Badge variant="secondary">{education.year}</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Certifications */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-8">Certifications</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {certifications.map((cert, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-200">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <Award size={24} className="text-[#2563eb]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#1e3a5f] mb-2">{cert.name}</h3>
                      <p className="text-gray-600 text-sm mb-2">{cert.issuer}</p>
                      <Badge variant="outline">{cert.year}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Awards & Recognition */}
        <div>
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-8">Awards & Recognition</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {awards.map((award, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-200">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-yellow-100 p-3 rounded-lg">
                      <Award size={24} className="text-yellow-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#1e3a5f] mb-2">{award.title}</h3>
                      <p className="text-gray-600 text-sm mb-2">{award.organization}</p>
                      <Badge variant="outline">{award.year}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;